import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useApplication } from '@/lib/api/hooks/application';
import { ActionDropdown, type DropdownAction } from '@/components/ui';
import { MapPin, Briefcase, Mail, Info, LinkedinIcon, Phone, ClockIcon, Book } from 'lucide-react';
import { Application } from '@/lib/api/types';
import UpdateApplicationStatusModal from '@/pages/opportunities/applications/modals/UpdateApplicationStatusModal';
import { initialsCase, titleCase } from '@/utils/helpers/textTransform';
import formatDate from '@/utils/helpers/formatDate';
import { Paragraph } from '@/components/ui/Typography';
import { DocumentIcon } from '@/assets/icons';
import handleDownloadFromUrl from '@/utils/helpers/handleDownloadFromUrl';

const ApplicationDetailPage: React.FC = () => {
    const { id: applicationId } = useParams<{ id: string }>();
    const navigate = useNavigate();

      const [updateStatusModalOpen, setUpdateStatusModalOpen] = useState(false);
      const [selectedApplicationForUpdateStatus, setSelectedApplicationForUpdateStatus] = useState<Application | null>(null);

    const { data: applicationData, isFetching, error, refetch } = useApplication(applicationId || '');
    const application = applicationData?.data;

    if (!applicationId) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold">Application ID missing</h2>
                <button onClick={() => navigate('/admin/applications')} className="mt-4 text-[#681628] hover:underline">
                    &larr; Back to Applications
                </button>
            </div>
        );
    }


      const handleUpdateApplicationStatus = (application: Application) => {
        setSelectedApplicationForUpdateStatus(application);
        setUpdateStatusModalOpen(true);
      };

        const confirmUpdateStatus = () => {
      setUpdateStatusModalOpen(false);
          setSelectedApplicationForUpdateStatus(null);
          refetch();
  };

    const getDropdownActions = (): DropdownAction[] => [
       {
        label: 'Update Status',
        onClick: (e) => {
          e.stopPropagation();
          handleUpdateApplicationStatus(application);
        }
      }
    ];

    const handleDownloadFile = (url: string) => {
        handleDownloadFromUrl({url});
    };

    const renderContactInformation = (application: Application) => {

                                const infoItems = [
                                              {
                                    icon: <MapPin className="w-5 h-5 text-wine-200" />,
                                    label: 'Location',
                                    value: application.country || 'N/A',
                                },
                                     {
                                    icon: <Phone className="w-5 h-5 text-wine-200" />,
                                    label: 'Phone Number',
                                    value: application.phoneNumber || 'N/A',
                                    type: 'phone',
                                },
                                {
                                    icon: <Briefcase className="w-5 h-5 text-wine-200" />,
                                    label: 'Application Type',
                                    value: application.portfolio || 'N/A',
                                    type: 'link',
                                },
                               
                                {
                                    icon: <LinkedinIcon className="w-5 h-5 text-blue-500" />,
                                    label: 'LinkedIn',
                                    value: application.linkedIn || 'N/A',
                                    type: 'link',
                                },
                                {
                                    icon: <Mail className="w-5 h-5 text-red-500" />,
                                    label: 'Submission Email',
                                    value: application.email || 'N/A',
                                    type: 'email',
                                },
                            ];

                            return       <div className="grid gap-3">
                                        {infoItems.map((item, i) => (
                                            <div key={i} className={`flex items-center gap-3`}>
                                                <div className="shrink-0 w-7 h-7 rounded-md bg-gray-50 flex items-center justify-center">
                                                    {item.icon}
                                                </div>
                                                
                                                <div className="min-w-0">
                                                  {
                                                    item.type === 'link' ? (
                                                        <a href={item.value} className="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer">
                                                            {item.value}
                                                        </a>
                                                    ) : item.type === 'email' ? (
                                                        <a href={`mailto:${item.value}`} className="text-blue-500 hover:underline">
                                                            {item.value}
                                                        </a>
                                                    ) : item.type === 'phone' ? (
                                                        <a href={`tel:${item.value}`} className="text-blue-500 hover:underline">
                                                            {item.value}
                                                        </a>
                                                    ) : (
                                                        <span className="text-gray-900">{item.value}</span>
                                                    )
                                                }
                                                </div>
                                            </div>
                                        ))}
                                    </div> 
    };

    if (isFetching) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#681628] mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading application...</p>
                </div>
            </div>
        );
    }

    if (error || !application) {
        return (
            <div className="text-center p-8 bg-white rounded-lg">
                <h2 className="text-xl font-bold text-red-600">Error loading application</h2>
                <p className="mt-2 text-gray-600">{error?.message || 'Application not found'}</p>
                <button onClick={() => navigate('/admin/applications')} className="mt-4 text-[#681628] hover:underline">
                    &larr; Back to Applications
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="space-y-6">

                {/* Breadcrumb */}
                  <div className="flex items-center gap-2 text-sm">
                                    <Link to="/admin/applications" className="text-forestGreen-100 hover:underline font-medium">
                                        Applications
                                    </Link>
                                    <span className="text-gray-400">▸</span>
                                    <span className="text-gray-600">{application.jobTitle}</span>
                                </div>

                {/* Header with Title and Actions */}
                <div className="flex items-start justify-between gap-6">
                    <div className="flex-1 flex flex-col gap-2">
                        <div className="flex items-center gap-4 ">
                            <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center font-medium text-base">
                                {initialsCase(`${application.firstName ?? ''} ${application.lastName ?? ''}`)}
                            </div>
                            <div className='flex items-center gap-8'>
                                <h1 className="text-2xl font-bold text-gray-900">{application.firstName ?? ""} {application.lastName ?? ""}</h1>
                                <span className={`flex items-center px-2 py-1 rounded-md text-sm font-normal ${application.status === 'INTERVIEW'
                                    ? 'bg-green-50 text-green-700'
                                    : 'bg-gray-100 text-gray-700'
                                }`}>
                                    ● {titleCase(application.status)}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600">
                                Applied for

                                </span>
                                <span className="font-semibold text-base text-gray-900"> {application.jobTitle}</span>
                            </div>
                            <span>|</span>
                            <div className="flex items-center gap-2 text-gray-600">
                                <span className="text-sm ">Applied On</span>
                                <span className="text-sm "> {formatDate(application.createdAt, "MMM DD, YYYY HH:mm A")}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-2">                      
                        <ActionDropdown
                            id={`application-${applicationId}`}
                            actions={getDropdownActions()}
                        />
                    </div>
                </div>

                {/* Main Content with Sidebar */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main Content Area */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Quick Info Bar */}
                      

                             
                                <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-2 gap-4">
                                    <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                                        <Paragraph className="text-gray-900 font-semibold mb-2">Contact Information</Paragraph>
                                        {
                                            renderContactInformation(application)
                                        }
                                   
                                    </div>
                                  
                                    <div className="grid grid-rows-2 gap-4">
                                          <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                                        <Paragraph className="text-gray-900 font-semibold mb-2">Resume</Paragraph>
                                        <div className="flex items-center gap-4">
                                            <DocumentIcon className="w-12 h-12 text-gray-600" />
                                            <a href={application.cvUrl} target="_blank" rel="noopener noreferrer" className=" text-wine-200 hover:underline">
                                                Preview
                                            </a>
                                            <span onClick={()=>handleDownloadFile(application.cvUrl)} className=" text-wine-200 hover:underline cursor-pointer">
                                                Download
                                            </span>
                                        </div>
                                        </div>
                                          <div className="space-y-4 p-4 border border-gray-200 rounded-lg">
                                        <Paragraph className="text-gray-900 font-semibold mb-2">Cover Letter</Paragraph>
                                        <div className="flex items-center gap-4">
                                            <DocumentIcon className="w-12 h-12 text-gray-600" />
                                            <a href={application.coverLetterUrl} target="_blank" rel="noopener noreferrer" className=" text-wine-200 hover:underline">
                                                Preview
                                            </a>
                                            <span onClick={()=>handleDownloadFile(application.coverLetterUrl)} className=" text-wine-200 hover:underline cursor-pointer">
                                                Download
                                            </span>
                                        </div>
                                        </div>
                                        </div>
                                </div>
                

                        {/* Work Experience */}
                        <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col gap-4">
                                <div className='flex items-center gap-2'>
                                    <div className='rounded-md h-10 w-10 flex items-center justify-center bg-[#E8F6EE]'>
                                <Briefcase className="w-5 h-5 text-[#0F8A47]" />

                                    </div>
 <h2 className="text-lg font-semibold text-gray-900 ">
                               Work Experience
                            </h2>
                                </div>
                           
                            <div>
                                {Array.isArray(application.workExperience) && application.workExperience.length > 0 ? (
                                    <ul className="space-y-4 list-none p-0">
                                        {application.workExperience.map((experience : {
                                            companyName: string,
                                            role: string,
                                            startYear: number,
                                            endYear: number,
                                            currentlyWorking: boolean,
                                            location: string,
                                            description: string,
                                            responsibilities: string[],
                                            achievements: string[]
                                        }, index: number) => (
                                            <li key={index} className="border border-gray-200 rounded-lg p-4">
                                                <div className="flex items-center justify-between">
                                                    <h3 className="text-base font-semibold text-gray-900">{experience.role}</h3>
                                                    <span className="text-sm text-gray-600">
                                                        {experience.startYear} - {experience.currentlyWorking ? 'Present' : experience.endYear}
                                                    </span>
                                                </div>
                                                <p className="text-sm text-gray-600">{experience.companyName}</p>
                                                <p className="text-sm text-gray-600">{experience.location}</p>
                                                <div className="mt-4">
                                                    <div className="flex flex-col">
                                                         <p className="text-sm font-medium text-gray-900">Description:</p>
                                                    <p className="text-sm text-gray-800">{experience.description}</p>
                                                    </div>
                                                      
                                                    {experience.responsibilities && experience.responsibilities.length > 0 && (
                                                        <div className="mt-2">
                                                            <p className="text-sm font-medium text-gray-900">Responsibilities:</p>
                                                            <ul className="list-disc list-inside text-sm text-gray-700">
                                                                {experience.responsibilities.map((resp, i) => (
                                                                    <li key={i}>{resp}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                    {experience.achievements && experience.achievements.length > 0 && (
                                                        <div className="mt-2">
                                                            <p className="text-sm font-medium text-gray-900">Achievements:</p>
                                                            <ul className="list-disc list-inside text-sm text-gray-700">
                                                                {experience.achievements.map((ach, i) => (
                                                                    <li key={i}>{ach}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    )}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-gray-600">No work experience available.</p>
                                )}
                            </div>
                        </div>
              
                        {/* Education */}
                        <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col gap-4">
                                <div className='flex items-center gap-2'>
                                    <div className='rounded-md h-10 w-10 flex items-center justify-center bg-[#E8F6EE]'>
                                <Book className="w-5 h-5 text-[#0F8A47]" />

                                    </div>
 <h2 className="text-lg font-semibold text-gray-900 ">
                               Education
                            </h2>
                                </div>
                           
                            <div>
                                {Array.isArray(application.education) && application.education.length > 0 ? (
                                    <ul className="space-y-4 list-none p-0">
                                        {application.education.map((edu:{
                                            degree: string,
                                            discipline: string,
                                            startYear: number,
                                            endYear: number,
                                            currentlyStudying: boolean,
                                            schoolName: string,
                                            location: string,
                                            description: string,
                                            optionGrade?: string
                                        }, index: number) => (
                                            <li key={index} className="border border-gray-200 rounded-lg p-4">
                                                <div className="flex items-center justify-between">
                                                    <h3 className="text-base font-semibold text-gray-900">{edu.degree} in {edu.discipline}</h3>
                                                    <span className="text-sm text-gray-600">
                                                        {edu.startYear} - {edu.currentlyStudying ? 'Present' : edu.endYear}
                                                    </span>
                                                </div>
                                                {
                                                    edu.optionGrade && <p className="text-sm italic text-gray-600">({edu.optionGrade})</p>
                                                }
                                                <p className="text-sm text-gray-600">{edu.schoolName}</p>
                                                <p className="text-sm text-gray-600">{edu.location}</p>
                                                <div className="mt-4">
                                                    <p className="text-sm font-medium text-gray-900">Description:</p>
                                                    <p className="text-sm text-gray-800">{edu.description}</p>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-sm text-gray-600">No education information available.</p>
                                )}
                            </div>
                        </div>

                  
                    </div>

                    {/* Sidebar */}
                    <div className="lg:col-span-1 space-y-4">
                        {/* Application Status */}
                        <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-4">
                            <div className='flex items-center gap-2 '>
                                <div className='rounded-md h-10 w-10 flex items-center justify-center bg-[#9333ea10]'>
                                  <ClockIcon className="w-5 h-5 text-[#e9ae38]" />

                            </div>
                            <h3 className="font-semibold text-gray-900">Application Status</h3>
                                </div>
                           <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between gap-4 text-sm text-gray-600">
                                <span>
                                    Status:</span><span className="font-medium text-gray-900">{application.status}</span>
                            </div>
                            <div className="flex items-center justify-between gap-4 text-sm text-gray-600">
                                <span>
                                    Date Applied:</span><span className="font-medium text-gray-900">{application.createdAt ? formatDate(application.createdAt, 'MMMM DD, YYYY HH:mm A') : 'N/A'}</span>
                            </div>
                            <div className="flex items-center justify-between gap-4 text-sm text-gray-600">
                                <span>
                                    Job ID:</span><span className="font-medium text-gray-900">{application.jobId}</span>
                            </div>
                           </div>

                        </div>


                        {/* Publishing Information */}
                        <div className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col gap-4">
                            <div className='flex items-center gap-2 '>
                                <div className='rounded-md h-10 w-10 flex items-center justify-center bg-[#9333ea10]'>
                                  <Info className="w-5 h-5 text-[#9333ea]" />

                                </div>
                                   <h3 className="font-semibold text-gray-900">
                               Review Information
                            </h3>
                            </div>
                           
                            <div className="space-y-3 text-sm">
                                <div>
                                    <p className="text-gray-500">Updated By</p>
                                    <p className="font-medium text-gray-900">{application.updatedBy || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-gray-500">Updated At</p>
                                    <p className="font-medium text-gray-900">{application.updatedAt ? formatDate(application.updatedAt, 'MMMM DD, YYYY HH:mm A') : 'N/A'}</p>
                                </div>
                                {/* <div>
                                    <p className="text-gray-500">Created By</p>
                                    <p className="font-medium text-gray-900">{application.createdBy || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-gray-500">Created On</p>
                                    <p className="font-medium text-gray-900">{application.createdAt ? formatDate(application.createdAt, 'MMMM DD, YYYY HH:mm A') : 'N/A'}</p>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

               {/* Update Application Status Modal */}
                  <UpdateApplicationStatusModal
                    isOpen={updateStatusModalOpen}
                    onClose={() => setUpdateStatusModalOpen(false)}
                    onSuccess={confirmUpdateStatus}
                    selectedApplication={selectedApplicationForUpdateStatus?.applicationId ? selectedApplicationForUpdateStatus.applicationId : null}
                  />
        </>
    );
};

export default ApplicationDetailPage;
